import { predefinedTemplates } from 'constants/entities';
import { HIERARCHY_DEFAULT_DEPTH } from 'constants/hierarchy';
import {
  BulkUpdateEntitiesRequest,
  EntityListResponse,
  ExportEntityRequest,
  IEntityAddMediaRequest,
  IEntityReportRequest,
  IEntitySearchRequest,
  ImportKMLPreviewRequest,
  ImportKMLRequest,
  UpdateEntityAccessRulesRequest,
  UpsertEntityRequest,
} from 'interfaces';
import { EntityParameter, EntityWithAccessRules } from 'types';

import axios from '../services/axios';

import { handleAxiosResponse } from './apiUtils';

export const GO_API_PREFIX = process.env.REACT_APP_GO_API_PREFIX ?? '/go';

export const getPredefinedTemplate = (templateTitle: predefinedTemplates) =>
  axios.get(`${GO_API_PREFIX}/entity-template/predefined/${templateTitle}`);

export const searchEntities = (params: IEntitySearchRequest) =>
  handleAxiosResponse<EntityListResponse>(
    axios.post(`${GO_API_PREFIX}/entity-V2/search`, params)
  );

export const searchMapEntities = (params: IEntitySearchRequest) =>
  axios.post(`${GO_API_PREFIX}/entity-V2/search`, {
    maxDepth: HIERARCHY_DEFAULT_DEPTH,
    ...params,
  });

export const deleteEntityNoHandle = (id: number) =>
  axios.delete(`${GO_API_PREFIX}/entity/${id}`, { params: { cascade: true } });

// todo remove after switch to new redux
export const deleteEntity = (id: number) =>
  handleAxiosResponse<number>(deleteEntityNoHandle(id));

export const upsertEntityNoHandle = (params: UpsertEntityRequest) =>
  axios.post<EntityWithAccessRules>(`${GO_API_PREFIX}/entity`, {
    ...params,
    createdBy: {},
  });

// todo remove after switch to new redux
export const upsertEntity = (params: UpsertEntityRequest) =>
  handleAxiosResponse<EntityWithAccessRules>(upsertEntityNoHandle(params));

export const getAllEntityTemplateParameters = (id: number) =>
  handleAxiosResponse<EntityParameter[]>(
    axios.get(`${GO_API_PREFIX}/entity-template/${id}/parameters`)
  );

export const buildReport = (params: IEntityReportRequest) =>
  axios.post(`${GO_API_PREFIX}/buildReport`, params);

export const addMediaToEntity = (payload: IEntityAddMediaRequest) =>
  axios.patch(
    `${GO_API_PREFIX}/entity/${payload.entityID}/media/${payload.paramID}`,
    payload.file,
    {
      headers: { 'Content-Type': 'application/octet-stream' },
      params: {
        fileName: payload.file.name,
        mediaArrayIndex: payload.mediaArrayIndex,
      },
    }
  );

export const exportEntity = (data: ExportEntityRequest) =>
  axios.post(`${GO_API_PREFIX}/entity/export`, data, { responseType: 'blob' });

export const upsertEntityAccessRules = (data: UpdateEntityAccessRulesRequest) =>
  axios.post(`${GO_API_PREFIX}/entity-V2/access-rules`, data);

export const deleteEntityAccessRules = (data: UpdateEntityAccessRulesRequest) =>
  axios.delete(`${GO_API_PREFIX}/entity-V2/access-rules`, { data });

export const relinkEntity = (entityID: number, newParentID?: number) =>
  axios.post(`${GO_API_PREFIX}/entity-V2/${entityID}/relink`, { newParentID });

export const importKML = (file: File, params: ImportKMLRequest) =>
  axios.post(`${GO_API_PREFIX}/entity-V2/${params.entityID}/import`, file, {
    headers: { 'Content-Type': 'application/octet-stream' },
    params: {
      file: params.file,
      filename: params.filename,
      ignoreMedia: params.ignoreMedia,
    },
  });

export const searchEntityById = (entityID: number) =>
  axios.get(`${GO_API_PREFIX}/entity-V2/search/${entityID}`);

export const bulkUpdateEntities = (data: BulkUpdateEntitiesRequest) =>
  axios.patch(`${GO_API_PREFIX}/entity-V2/bulk`, data);

export const importKMLPreview = (file: File, params: ImportKMLPreviewRequest) =>
  axios.post(`${GO_API_PREFIX}/entity-V2/import/preview`, file, {
    headers: { 'Content-Type': 'application/octet-stream' },
    params: {
      file: params.file,
      filename: params.filename,
    },
  });

export const regroupEntityById = (entityID: number) =>
  axios.post(`${GO_API_PREFIX}/entity-V2/${entityID}/regroup`);

// unused for now, will be used for timeline histogram later
export const getEntitiesHistogram = () =>
  axios.get(`${GO_API_PREFIX}/entity-V2/histogram`);
